// Shared valibot schemas for wire DTOs, applied at the network boundary by
// `getValidated` purely to reject malformed *envelopes* — the consumer-facing
// types are the hand-written DTOs in `src/types/api.ts`, not these schemas'
// `InferOutput` (see the note in `utils/api.ts`).
//
// Two deliberate leniencies, both required to avoid rejecting legitimate responses:
//   • Leaf objects use `v.looseObject`, NOT `v.object`: the real payloads are far
//     richer than the declared DTOs (the grid/list read playerName, pictureUrl,
//     scoreStats, stats, prices, … via casts). `v.object` would STRIP those keys
//     from the output and blank the UI; `looseObject` passes them through.
//   • Leaf fields use `v.nullish`, NOT `v.optional`: the API maps nullable DB
//     columns straight through, emitting literal `null` (e.g. `age`, `clubName`,
//     `position` for free agents / missing data). `v.optional` accepts only
//     `undefined` and would reject those responses; `v.nullish` accepts
//     `null | undefined | value` while still rejecting a wrong TYPE.

import * as v from 'valibot';

export const statsSchema = v.looseObject({
  games: v.nullish(v.number()),
  average: v.nullish(v.number()),
  total: v.nullish(v.number()),
  last15: v.nullish(v.number()),
  last30: v.nullish(v.number()),
  all: v.nullish(v.number()),
});

export const priceSchema = v.looseObject({
  price: v.nullish(v.number()),
  currency: v.nullish(v.string()),
  cheapestPrice: v.nullish(
    v.looseObject({
      price: v.nullish(v.number()),
      slug: v.nullish(v.string()),
    }),
  ),
});

// Combined `PlayerBase` + `Player` wire fields, all nullable/optional.
export const playerSchema = v.looseObject({
  // PlayerBase
  slug: v.nullish(v.string()),
  name: v.nullish(v.string()),
  displayName: v.nullish(v.string()),
  age: v.nullish(v.number()),
  position: v.nullish(v.string()),
  birthDate: v.nullish(v.string()),
  pictureUrl: v.nullish(v.string()),
  competitionCovered: v.nullish(v.boolean()),
  // Player
  id: v.nullish(v.string()),
  countryCode: v.nullish(v.string()),
  clubName: v.nullish(v.string()),
  clubCountryCode: v.nullish(v.string()),
  clubPictureUrl: v.nullish(v.string()),
  leagueName: v.nullish(v.string()),
  competitionName: v.nullish(v.string()),
});

export const cardSchema = v.looseObject({
  cardSlug: v.nullish(v.string()),
  name: v.nullish(v.string()),
  rarity: v.nullish(v.string()),
  price: v.nullish(v.number()),
  lastAuctionPrice: v.nullish(v.number()),
  cheapestMarketPrice: v.nullish(v.number()),
});

/** `{ items: T[] }` envelope with a required `items` array (replaces the old `Array.isArray` guards). */
export const itemsDataSchema = <S extends v.GenericSchema>(item: S) =>
  v.looseObject({ items: v.array(item) });
